/**
 * Human-readable one-liners for contract-fit entries — single source for the
 * Building-blocks section preview and the Example details view.
 */

import type { ContractFitEntry } from '@modules/shared-types';

export function fitLine(entry: ContractFitEntry): string {
  if (entry.kind === 'output') {
    return entry.resolution === 'bind' && entry.targetVarId
      ? `📤 ${entry.exampleVarId} (output) → binds to ${entry.targetVarId}`
      : `📤 ${entry.exampleVarId} (output) → new · created`;
  }
  if (entry.resolution === 'bind' && entry.targetVarId) {
    return `✅ ${entry.exampleVarId} → maps to ${entry.targetVarId} (values compatible)`;
  }
  if (entry.resolution === 'needs-adapt') {
    return `⚠ ${entry.exampleVarId} → existing variable differs — the adaptation rebinds it`;
  }
  return `➕ ${entry.exampleVarId} → will be created`;
}

/** One row's variable-fit verdict — the library table cell and the details caption. */
export interface ContractFitSummary {
  /** Nothing the adaptation has to rebind. */
  fits: boolean;
  /** «var (value, value)» per needs-adapt entry — the uncovered values named. */
  needsAdapt: string[];
  /** Row label: «fits» or «needs adapt · primary_goal (sleep)». */
  label: string;
}

/**
 * Invariant 10: a needs-adapt row is an honest lock — it NAMES the variables
 * (and the values) the strategy does not cover, never a silent disable.
 */
export function fitSummary(entries: ContractFitEntry[]): ContractFitSummary {
  const needsAdapt = entries
    .filter((entry) => entry.resolution === 'needs-adapt')
    .map((entry) => ((entry.referencedValues?.length ?? 0) > 0
      ? `${entry.exampleVarId} (${entry.referencedValues!.join(', ')})`
      : entry.exampleVarId));
  return {
    fits: needsAdapt.length === 0,
    needsAdapt,
    label: needsAdapt.length === 0 ? 'fits' : `needs adapt · ${needsAdapt.join(', ')}`,
  };
}
